import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const CrackCUETSection = () => (
  <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 my-10">
    <h2 className="text-xl font-bold text-blue-900 mb-3">How Crack CUET Helps Get Better CUET Percentiles</h2>
    <p className="text-gray-700 leading-relaxed">
      Crack CUET is India's most focused CUET mock test platform, specifically designed to help aspirants targeting Delhi University's top colleges. Our AI-powered mock tests cover all 15 CUET subjects in the exact NTA pattern — 50 questions, +5/−1 marking, 60-minute timed tests. After every mock, you get a national percentile rank that tells you exactly where you stand relative to other DU aspirants. Our question bank includes Economics, Business Studies, Accountancy, History, Political Science, Sociology, and all other domain subjects tested for DU programmes — designed by toppers who themselves secured seats at SRCC, St. Stephen's, and Hindu College. Practice with Crack CUET to close the gap between your current score and your target college's cutoff.
    </p>
    <p className="mt-5 text-center">
      <Link to="/dashboard" className="inline-block bg-blue-600 text-white px-6 py-2.5 rounded-lg font-semibold text-sm hover:bg-blue-700 transition-colors">
        Start Your DU Prep — Free Mock Included
      </Link>
    </p>
  </div>
);

const CutoffRow = ({ programme, general, obc, sc, st }) => (
  <tr className="border-b border-gray-100">
    <td className="px-3 py-2 text-gray-800 text-sm font-medium">{programme}</td>
    <td className="px-3 py-2 text-gray-700 text-sm text-center">{general}</td>
    <td className="px-3 py-2 text-gray-600 text-sm text-center">{obc}</td>
    <td className="px-3 py-2 text-gray-600 text-sm text-center">{sc}</td>
    <td className="px-3 py-2 text-gray-600 text-sm text-center">{st}</td>
  </tr>
);

export default function BlogDUCutoffs2026() {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>DU CUET Cutoffs 2026 — SRCC, St. Stephen's, Hindu, Miranda House | Crack CUET</title>
        <meta name="description" content="Expected and previous year DU CUET cutoffs for 2026 — SRCC, St. Stephen's, Hindu College, Miranda House, Hansraj, Kirorimal and all top Delhi University colleges." />
        <meta name="keywords" content="DU CUET cutoff 2026, SRCC CUET cutoff, Hindu College cutoff, Miranda House cutoff, Delhi University admission 2026" />
        <link rel="canonical" href="https://crackcuet.com/blog/du-cutoffs-cuet-2026-srcc-stephens-hindu-miranda" />
        <meta property="og:title" content="DU CUET Cutoffs 2026 — SRCC, St. Stephen's, Hindu, Miranda House" />
        <meta property="og:description" content="Expected CUET 2026 cutoffs for all top DU colleges including SRCC, St. Stephen's, Hindu, and Miranda House." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://crackcuet.com/blog/du-cutoffs-cuet-2026-srcc-stephens-hindu-miranda" />
      </Helmet>
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center"><img src="/logo-full.png" alt="Crack CUET" className="h-16" /></Link>
          <Link to="/blog" className="text-sm text-gray-500 hover:text-gray-900">All Blogs</Link>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-4 py-12">
        <div className="mb-8">
          <span className="text-sm font-medium text-blue-600">Admissions</span>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-2 mb-4 leading-tight">
            DU CUET Cutoffs 2026 — Expected Scores for SRCC, St. Stephen's, Hindu, Miranda House &amp; More
          </h1>
          <div className="flex items-center gap-4 text-sm text-gray-400">
            <span>Feb 20, 2026</span>
            <span>9 min read</span>
            <span>By Crack CUET Team</span>
          </div>
        </div>

        <img
          src="https://images.unsplash.com/photo-1680084521631-e4e6d77704d8?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85"
          alt="Delhi University campus buildings DU CUET admission cutoffs 2026"
          className="w-full rounded-xl mb-8 object-cover max-h-80"
        />

        <div className="prose prose-gray max-w-none text-gray-700 leading-relaxed space-y-6">
          <p>
            Delhi University's CSAS (Common Seat Allocation System) allocates seats based entirely on <strong>CUET scores</strong> — no board marks, no interviews, no extra-curricular weighting. This makes the CUET score the single most important number for every DU aspirant. But how high do you actually need to score to get into SRCC? Or St. Stephen's? Or Hindu College?
          </p>
          <p>
            This guide compiles <strong>expected CUET cutoffs for DU 2026</strong> based on 2024 and 2025 trend analysis, with programme-specific cutoff data for the most sought-after colleges and courses.
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 my-6">
            <p className="text-amber-800 font-semibold mb-1">Disclaimer</p>
            <p className="text-amber-700 text-sm">All cutoffs below are <strong>estimates based on 2024–25 data</strong>. Actual 2026 cutoffs depend on the number of applicants, difficulty of the 2026 CUET paper, and seat availability. Use these as targets, not guarantees. Always verify with official DU CSAS data when released.</p>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mt-10">How DU CUET Cutoffs Work</h2>
          <p>
            DU uses a <strong>percentile-based allocation system</strong>. The CUET score (raw marks out of 250 per subject) is converted to a percentile based on that specific test's performance distribution. DU then creates a <strong>Best of N subjects score</strong> — typically the language score + best domain subject(s) — depending on the programme's requirement.
          </p>
          <p>
            For example, for <strong>B.Com (Hons) at SRCC</strong>:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Required subjects: English + Business Studies + Accountancy + Mathematics/Economics</li>
            <li>DU takes the best 4 subjects score to calculate your combined CUET score</li>
            <li>The cutoff is the minimum combined score at which the last student was allocated a seat</li>
          </ul>
          <p>
            This means your strategy should include <strong>maximising at least 4 subjects</strong> — your language(s) + 2–3 domain subjects relevant to your target programmes.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-10">SRCC — Shri Ram College of Commerce</h2>
          <p>SRCC is India's most competitive commerce college. The cutoff has been rising steadily year-on-year.</p>
          <div className="overflow-x-auto rounded-lg border border-gray-200 my-4">
            <table className="min-w-full text-sm border-collapse">
              <thead>
                <tr className="bg-blue-50">
                  <th className="px-3 py-2 text-left font-semibold text-blue-800 border-b border-blue-200">Programme</th>
                  <th className="px-3 py-2 text-center font-semibold text-blue-800 border-b border-blue-200">General</th>
                  <th className="px-3 py-2 text-center font-semibold text-blue-800 border-b border-blue-200">OBC</th>
                  <th className="px-3 py-2 text-center font-semibold text-blue-800 border-b border-blue-200">SC</th>
                  <th className="px-3 py-2 text-center font-semibold text-blue-800 border-b border-blue-200">ST</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                <CutoffRow programme="B.Com (Hons)" general="775–800+" obc="730–760" sc="680–710" st="640–670" />
                <CutoffRow programme="BA (Hons) Economics" general="780–800+" obc="740–765" sc="690–720" st="650–680" />
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-400 italic">Scores out of 800 (Best of 4 subjects × 200). These are estimated combined CUET scores.</p>
          <p>
            SRCC's B.Com (Hons) has been the most competitive seat in DU for five consecutive years. For General category applicants, a combined score of 775+ across 4 subjects (average 194/200 per subject) is almost mandatory. This translates to a <strong>~97th percentile</strong> requirement in each tested subject.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-10">St. Stephen's College</h2>
          <p>
            St. Stephen's conducts its own interview round for a portion of seats (under the Christian minority quota), but the majority of seats are filled through the regular DU CSAS process based purely on CUET scores.
          </p>
          <div className="overflow-x-auto rounded-lg border border-gray-200 my-4">
            <table className="min-w-full text-sm border-collapse">
              <thead>
                <tr className="bg-blue-50">
                  <th className="px-3 py-2 text-left font-semibold text-blue-800 border-b border-blue-200">Programme</th>
                  <th className="px-3 py-2 text-center font-semibold text-blue-800 border-b border-blue-200">General</th>
                  <th className="px-3 py-2 text-center font-semibold text-blue-800 border-b border-blue-200">OBC</th>
                  <th className="px-3 py-2 text-center font-semibold text-blue-800 border-b border-blue-200">SC</th>
                  <th className="px-3 py-2 text-center font-semibold text-blue-800 border-b border-blue-200">ST</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                <CutoffRow programme="BA (Hons) Economics" general="760–790" obc="720–750" sc="680–710" st="640–665" />
                <CutoffRow programme="BA (Hons) History" general="740–770" obc="700–730" sc="660–690" st="620–650" />
                <CutoffRow programme="B.Sc (Hons) Physics" general="755–785" obc="715–745" sc="675–705" st="635–660" />
                <CutoffRow programme="B.Sc (Hons) Chemistry" general="740–770" obc="700–728" sc="660–690" st="620–645" />
              </tbody>
            </table>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mt-10">Hindu College</h2>
          <p>
            Hindu College is one of DU's most balanced and prestigious colleges — competitive across all three streams (science, commerce, humanities). It's a top target for students who want the full college experience alongside academic rigour.
          </p>
          <div className="overflow-x-auto rounded-lg border border-gray-200 my-4">
            <table className="min-w-full text-sm border-collapse">
              <thead>
                <tr className="bg-blue-50">
                  <th className="px-3 py-2 text-left font-semibold text-blue-800 border-b border-blue-200">Programme</th>
                  <th className="px-3 py-2 text-center font-semibold text-blue-800 border-b border-blue-200">General</th>
                  <th className="px-3 py-2 text-center font-semibold text-blue-800 border-b border-blue-200">OBC</th>
                  <th className="px-3 py-2 text-center font-semibold text-blue-800 border-b border-blue-200">SC</th>
                  <th className="px-3 py-2 text-center font-semibold text-blue-800 border-b border-blue-200">ST</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                <CutoffRow programme="B.Sc (Hons) Mathematics" general="760–790" obc="715–745" sc="675–700" st="630–655" />
                <CutoffRow programme="BA (Hons) Economics" general="755–785" obc="710–740" sc="670–700" st="625–655" />
                <CutoffRow programme="BA (Hons) English" general="745–775" obc="700–732" sc="660–690" st="618–645" />
                <CutoffRow programme="BA (Hons) History" general="735–765" obc="695–725" sc="655–685" st="610–638" />
                <CutoffRow programme="B.Com (Hons)" general="750–780" obc="705–735" sc="665–695" st="620–648" />
              </tbody>
            </table>
          </div>

          <img
            src="https://images.unsplash.com/photo-1680084521816-cc1ad0433ceb?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85"
            alt="Delhi University North Campus college building"
            className="w-full rounded-xl my-8 object-cover max-h-72"
          />

          <h2 className="text-2xl font-bold text-gray-900 mt-10">Miranda House &amp; Lady Shri Ram (LSR)</h2>
          <p>
            Miranda House and LSR are women's colleges that rank among India's best. Miranda House has consistently topped the NIRF rankings for women's colleges. Both have highly competitive cutoffs.
          </p>
          <div className="overflow-x-auto rounded-lg border border-gray-200 my-4">
            <table className="min-w-full text-sm border-collapse">
              <thead>
                <tr className="bg-blue-50">
                  <th className="px-3 py-2 text-left font-semibold text-blue-800 border-b border-blue-200">College &amp; Programme</th>
                  <th className="px-3 py-2 text-center font-semibold text-blue-800 border-b border-blue-200">General</th>
                  <th className="px-3 py-2 text-center font-semibold text-blue-800 border-b border-blue-200">OBC</th>
                  <th className="px-3 py-2 text-center font-semibold text-blue-800 border-b border-blue-200">SC</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {[
                  ['Miranda House — BA (Hons) Economics', '755–785', '710–740', '668–698'],
                  ['Miranda House — BA (Hons) History', '735–760', '690–720', '648–678'],
                  ['Miranda House — B.Sc (Hons) Zoology', '740–765', '695–722', '652–682'],
                  ['LSR — BA (Hons) Economics', '750–780', '705–735', '663–693'],
                  ['LSR — B.Com (Hons)', '748–775', '700–730', '658–688'],
                  ['LSR — BA (Hons) English', '742–770', '696–726', '652–682'],
                ].map(([prog, g, o, sc], i) => (
                  <tr key={i} className={`border-b border-gray-100 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                    <td className="px-3 py-2 text-gray-800 text-sm font-medium">{prog}</td>
                    <td className="px-3 py-2 text-gray-700 text-sm text-center">{g}</td>
                    <td className="px-3 py-2 text-gray-600 text-sm text-center">{o}</td>
                    <td className="px-3 py-2 text-gray-600 text-sm text-center">{sc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mt-10">Other Top DU Colleges — Quick Reference</h2>
          <div className="overflow-x-auto rounded-lg border border-gray-200 my-4">
            <table className="min-w-full text-sm border-collapse">
              <thead>
                <tr className="bg-blue-50">
                  <th className="px-3 py-2 text-left font-semibold text-blue-800 border-b border-blue-200">College</th>
                  <th className="px-3 py-2 text-left font-semibold text-blue-800 border-b border-blue-200">Known For</th>
                  <th className="px-3 py-2 text-center font-semibold text-blue-800 border-b border-blue-200">Approx. General Cutoff</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Hansraj College', 'Science (PCM, PCB), B.Com', '720–760'],
                  ['Ramjas College', 'Mathematics, Physics, Economics', '715–750'],
                  ['Kirori Mal College', 'Commerce, Humanities', '700–740'],
                  ['Deshbandhu College', 'Science, Mathematics', '695–730'],
                  ['Shaheed Bhagat Singh College', 'Commerce, Economics', '700–738'],
                  ['Daulat Ram College', 'Women\'s — Humanities & Science', '695–730'],
                  ['Kamala Nehru College', 'Women\'s — Commerce & Humanities', '695–728'],
                ].map(([college, known, cutoff], i) => (
                  <tr key={i} className={`border-b border-gray-100 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                    <td className="px-3 py-2 text-gray-800 text-sm font-medium">{college}</td>
                    <td className="px-3 py-2 text-gray-600 text-sm">{known}</td>
                    <td className="px-3 py-2 text-gray-700 text-sm text-center font-semibold">{cutoff}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mt-10">How to Set Your Target Score</h2>
          <p>Use this framework to set a realistic CUET score target:</p>
          <ol className="list-decimal pl-6 space-y-2">
            <li><strong>Identify your 3 dream colleges and 2 safe colleges</strong> — use the cutoff data above</li>
            <li><strong>Find the minimum combined CUET score</strong> needed for the highest-cutoff programme you're targeting</li>
            <li><strong>Add a 20–25 point buffer</strong> — cutoffs can rise year-on-year; don't aim exactly at the previous year's cutoff</li>
            <li><strong>Break down the combined score by subject</strong> — figure out the minimum you need per subject paper</li>
            <li><strong>Take a baseline mock test</strong> on Crack CUET — see where you stand right now vs your target</li>
            <li><strong>Track your percentile improvement</strong> across mocks to validate that your preparation is moving in the right direction</li>
          </ol>

          <CrackCUETSection />

          <h2 className="text-2xl font-bold text-gray-900 mt-10">Final Thoughts</h2>
          <p>
            CUET 2026 cutoffs at DU's top colleges will be competitive — they always are. But the difference between getting in and missing out is often just 10–15 marks across your subject papers. That gap is absolutely closeable with structured mock practice, targeted weak-area revision, and a clear understanding of the exam's marking scheme. Your dream college is within reach — the strategy is simply to start early and measure your progress honestly.
          </p>
        </div>
      </article>
    </div>
  );
}
